import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../../utils/api/axiosInstance";
import PageSeo from "../../components/common/PageSeo";
import styles from "../../styles/pages/auth/ResetPasswordPage.module.css";
import { FaArrowLeft } from "react-icons/fa";

const API_RESET_PASSWORD = (token) => `/authuser/reset-password/${token}`;

// 🔧 add cleaner (removes zero-width chars + trims)
const clean = (s) =>
  (s ?? "").normalize("NFKC").replace(/[\u200B-\u200D\uFEFF]/g, "").trim();

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const redirectTimerRef = useRef(null);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // "success" | "error"
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setMessage("Invalid or expired reset link.");
      setMessageType("error");
      return;
    }

    // 🔧 compare normalized values
    const p = clean(password);
    const c = clean(passwordConfirm);
    if (p !== c) {
      setMessage("Passwords do not match");
      setMessageType("error");
      return;
    }

    try {
      setLoading(true);
      await api.post(API_RESET_PASSWORD(token), { password: p, passwordConfirm: c }); // 🔧 send cleaned value
      setMessage("Password reset successful! Redirecting...");
      setMessageType("success");
      redirectTimerRef.current = setTimeout(() => navigate("/login?mode=login"), 2000);
    } catch (err) {
      setMessage(err.response?.data?.message || "Reset failed");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pageContainer">
      <PageSeo
        title="Reset Password"
        description="Choose a new password for your Simpary account."
        pathname={token ? `/reset-password/${token}` : "/reset-password"}
        noIndex
      />
      <div className="innerContainer">
        <div className={styles.resetContainer}>
          <div className={styles.resetWrapper}>
            <div className={styles.resetBox}>
              <h2>Reset Password</h2>
              <form onSubmit={handleSubmit}>
                <input
                  type="password"
                  placeholder="New Password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (message) {
                      setMessage(""); // clear stale mismatch
                      setMessageType("");
                    }
                  }}
                  required
                  autoComplete="new-password"
                />
                <input
                  type="password"
                  placeholder="Confirm Password"
                  value={passwordConfirm}
                  onChange={(e) => {
                    setPasswordConfirm(e.target.value);
                    if (message) {
                      setMessage(""); // clear stale mismatch
                      setMessageType("");
                    }
                  }}
                  required
                  autoComplete="new-password"
                />
                <button className="buttonPrimary" disabled={loading}>
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </form>
              {message && (
                <p
                  className={`${styles.message} ${
                    messageType === "success" ? styles.messageSuccess : styles.messageError
                  }`}
                  role={messageType === "error" ? "alert" : "status"}
                  aria-live={messageType === "error" ? "assertive" : "polite"}
                >
                  {message}
                </p>
              )}

              <button
                onClick={() => navigate("/login?mode=login")}
                className={styles.backLink}
              >
                <FaArrowLeft />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
